import React, { useEffect } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import { Bold, Italic, List, ListOrdered, Heading1, Heading2, Heading3, Link as LinkIcon, Image as ImageIcon, Quote, Undo, Redo, Code } from "lucide-react";

export default function RichEditor({ value, onChange }) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3] }, link: false }),
      Link.configure({ openOnClick: false, HTMLAttributes: { class: "text-[#FF4F00] font-bold underline" } }),
      Image,
    ],
    content: value || "<p></p>",
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
    editorProps: {
      attributes: {
        class: "prose-vibe min-h-[400px] p-4 outline-none",
        "data-testid": "rich-editor",
      },
    },
  });

  useEffect(() => {
    if (!editor) return;
    if (editor.getHTML() !== value && value !== undefined) {
      editor.commands.setContent(value || "<p></p>", false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  if (!editor) return <div className="border-2 border-black p-4 text-zinc-500">Loading editor…</div>;

  const Btn = ({ onClick, active, label, children, testid }) => (
    <button type="button" onClick={onClick} title={label} data-testid={testid}
      className={`p-2 border-2 border-black ${active ? "bg-black text-white" : "bg-white hover:bg-zinc-100"}`}>
      {children}
    </button>
  );

  const setLink = () => {
    const prev = editor.getAttributes("link").href || "";
    const url = window.prompt("URL", prev);
    if (url === null) return;
    if (url === "") { editor.chain().focus().unsetLink().run(); return; }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };

  const setImage = () => {
    const url = window.prompt("Image URL");
    if (!url) return;
    editor.chain().focus().setImage({ src: url }).run();
  };

  return (
    <div className="border-2 border-black bg-white">
      <div className="flex flex-wrap gap-1 p-2 border-b-2 border-black bg-zinc-50">
        <Btn label="H1" testid="re-h1" active={editor.isActive("heading", { level: 1 })} onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}><Heading1 size={14} /></Btn>
        <Btn label="H2" testid="re-h2" active={editor.isActive("heading", { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}><Heading2 size={14} /></Btn>
        <Btn label="H3" testid="re-h3" active={editor.isActive("heading", { level: 3 })} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}><Heading3 size={14} /></Btn>
        <span className="w-px bg-zinc-300 mx-1" />
        <Btn label="Bold" testid="re-bold" active={editor.isActive("bold")} onClick={() => editor.chain().focus().toggleBold().run()}><Bold size={14} /></Btn>
        <Btn label="Italic" testid="re-italic" active={editor.isActive("italic")} onClick={() => editor.chain().focus().toggleItalic().run()}><Italic size={14} /></Btn>
        <Btn label="Code" testid="re-code" active={editor.isActive("code")} onClick={() => editor.chain().focus().toggleCode().run()}><Code size={14} /></Btn>
        <span className="w-px bg-zinc-300 mx-1" />
        <Btn label="Bulleted list" testid="re-ul" active={editor.isActive("bulletList")} onClick={() => editor.chain().focus().toggleBulletList().run()}><List size={14} /></Btn>
        <Btn label="Numbered list" testid="re-ol" active={editor.isActive("orderedList")} onClick={() => editor.chain().focus().toggleOrderedList().run()}><ListOrdered size={14} /></Btn>
        <Btn label="Quote" testid="re-quote" active={editor.isActive("blockquote")} onClick={() => editor.chain().focus().toggleBlockquote().run()}><Quote size={14} /></Btn>
        <span className="w-px bg-zinc-300 mx-1" />
        <Btn label="Link" testid="re-link" onClick={setLink}><LinkIcon size={14} /></Btn>
        <Btn label="Image" testid="re-image" onClick={setImage}><ImageIcon size={14} /></Btn>
        <span className="w-px bg-zinc-300 mx-1" />
        <Btn label="Undo" testid="re-undo" onClick={() => editor.chain().focus().undo().run()}><Undo size={14} /></Btn>
        <Btn label="Redo" testid="re-redo" onClick={() => editor.chain().focus().redo().run()}><Redo size={14} /></Btn>
      </div>
      <EditorContent editor={editor} />
      <style>{`
        .prose-vibe h1 { font-family: 'Outfit', sans-serif; font-weight: 900; font-size: 2rem; margin: 1.25rem 0 0.5rem; letter-spacing: -0.02em; }
        .prose-vibe h2 { font-family: 'Outfit', sans-serif; font-weight: 800; font-size: 1.5rem; margin: 1rem 0 0.5rem; letter-spacing: -0.02em; }
        .prose-vibe h3 { font-family: 'Outfit', sans-serif; font-weight: 700; font-size: 1.25rem; margin: 0.75rem 0 0.5rem; }
        .prose-vibe p { margin: 0.5rem 0; line-height: 1.7; }
        .prose-vibe ul { list-style: disc; padding-left: 1.5rem; margin: 0.5rem 0; }
        .prose-vibe ol { list-style: decimal; padding-left: 1.5rem; margin: 0.5rem 0; }
        .prose-vibe li { margin: 0.25rem 0; }
        .prose-vibe blockquote { border-left: 4px solid #0A0A0A; padding-left: 1rem; margin: 0.75rem 0; font-style: italic; color: #52525B; }
        .prose-vibe code { background: #F4F4F5; padding: 0.1rem 0.3rem; font-family: 'JetBrains Mono', monospace; font-size: 0.9em; border: 1px solid #E4E4E7; }
        .prose-vibe img { max-width: 100%; border: 2px solid #0A0A0A; margin: 0.75rem 0; }
        .prose-vibe a { color: #FF4F00; font-weight: 700; text-decoration: underline; }
      `}</style>
    </div>
  );
}
